// src/lib/resources/benchmarks/paid-social.ts

/**
 * @file Paid-social CPM / CPC / CTR benchmark datasets + selection resolver
 * @description
 * Cross-platform paid-social benchmark bands consumed by the shared benchmark
 * engine (`./compare`) and the Paid Social Benchmarks analyzer. Same contract
 * as `./data`: every band keeps real `sources` (publisher + year), anchors are
 * quoted at the SOURCE'S OWN PRECISION (a cited 0.42% is stored as 0.42, never
 * rounded to 0.4), and any derived anchor is labelled in `note` (and flagged
 * `directional` when the whole band is an aggregate rather than a published
 * low–high spread).
 *
 * Band semantics differ by source and are stated per segment:
 *   - WordStream/LocaliQ segments: `low`/`high` are the LOWEST and HIGHEST
 *     per-industry medians in the cited report; `median` is the published
 *     cross-industry figure (their "averages" are medians by methodology).
 *   - Digital Applied segments: same industry-span semantics, Q1 2026 medians.
 *   - Aggregate segments (LinkedIn CPM, YouTube CPM): the source publishes a
 *     median band ("$31–$38") or a "most advertisers" band ("$5–$10") with no
 *     central anchor; the stored `median` is the band midpoint, the segment is
 *     flagged `directional`, and the note says so.
 *
 * Deliberate OMISSIONS (no current trustworthy published band — omit rather
 * than carry a stale or junk number):
 *   - YouTube CPC: the only current figure is a full observed span
 *     ($0.05–$10.71, AdConversion via Store Growers 2026) — an observed span
 *     is not a typical band, so it would produce meaningless verdicts.
 *   - Per-industry rows: the glossary `cpm` / `cpc` / `ctr` entries own the
 *     per-industry benchmark tables (`segmentBenchmarks`); this dataset stays
 *     at platform × objective grain so the two surfaces never duplicate.
 *   - X/Twitter, Pinterest, Snapchat: no current source of WordStream/
 *     Metricool-class quality found at review time (2026-07).
 *
 * Refresh cadence: these figures were re-verified against the cited reports on
 * 2026-07-12. Sources publish annually (WordStream/LocaliQ) or continuously
 * (Lebesgue, Triple Whale) — re-verify roughly quarterly and whenever a cited
 * publisher ships a new report year. When refreshing, also update the
 * `@preserve` hand-curated crawler twin at
 * `public/resources/tools/analyzers/paid-social-benchmarks.md`, which embeds
 * these tables verbatim (guarded by paid-social-md-twin-parity.test.ts).
 */

import type { BenchmarkSegment, BenchmarkSet } from './compare'

const SITE = 'https://www.adsights.ai'

/** Stable platform ids (segment `platform` values) in display order. */
export const PAID_SOCIAL_PLATFORM_IDS = ['meta', 'tiktok', 'linkedin', 'youtube'] as const
export type PaidSocialPlatformId = (typeof PAID_SOCIAL_PLATFORM_IDS)[number]

/** Human labels for the platform selector and SSR tables. */
export const PAID_SOCIAL_PLATFORM_LABELS: Record<PaidSocialPlatformId, string> = {
  meta: 'Meta (Facebook)',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
}

/** Metric ids in display order (also the keys of PAID_SOCIAL_METRICS). */
export const PAID_SOCIAL_METRIC_IDS = ['cpm', 'cpc', 'ctr'] as const
export type PaidSocialMetricId = (typeof PAID_SOCIAL_METRIC_IDS)[number]

/**
 * CPM (cost per 1,000 impressions), USD. LOWER is better — the engine flips
 * the verdict direction via `higherIsBetter: false`.
 */
export const PAID_SOCIAL_CPM_BENCHMARK: BenchmarkSet = {
  id: 'paid-social-cpm',
  metricLabel: 'CPM',
  unit: 'currency',
  higherIsBetter: false,
  glossaryUrl: `${SITE}/resources/glossary/metrics/cost-per-mille-cpm`,
  segments: [
    {
      id: 'meta',
      platform: 'meta',
      segment: 'Meta (Facebook) · all objectives',
      low: 7.19,
      median: 11.54,
      high: 14.68,
      sources: ['Digital Applied 2026 Facebook Ads Benchmarks (Q1 2026 medians)'],
      note: 'Objective drives the band: reach campaigns median $7.19, conversion campaigns $14.68, cross-industry average $11.54. Lebesgue’s 2026 per-industry spread ($6.96–$12.46) sits inside the same range.',
    },
    {
      id: 'tiktok',
      platform: 'tiktok',
      segment: 'TikTok · In-Feed',
      low: 5.40,
      median: 9.16,
      high: 14.10,
      sources: ['Digital Applied 2026 TikTok Ads Benchmarks (in-feed industry span)'],
      note: 'Dataset mix moves TikTok CPM a lot: Lebesgue’s 2026 conversion-optimized e-commerce average is $4.80 (typical $3–$15) while Triple Whale’s 2025 e-commerce dataset averages $13.26 — both fall inside this band.',
    },
    {
      id: 'linkedin',
      platform: 'linkedin',
      segment: 'LinkedIn · Sponsored Content',
      low: 31,
      median: 34.5,
      high: 38,
      directional: true,
      sources: ['Closely 2026 LinkedIn Ad Benchmarks (aggregated 2025 reports)'],
      note: 'Directional — the source publishes a median band ($31–$38), not a low–high spread; the stored median is the band midpoint. High-competition sectors (SaaS, healthcare) run $50–$100.',
    },
    {
      id: 'youtube',
      platform: 'youtube',
      segment: 'YouTube · skippable in-stream',
      low: 5,
      median: 7.5,
      high: 10,
      directional: true,
      sources: ['Store Growers 2026 YouTube Ads Benchmarks (updated Feb 2026)'],
      note: 'Directional — cited as “$5–$10 for most advertisers” with no central anchor; the stored median is the band midpoint. Connected-TV inventory prices materially higher.',
    },
  ],
}

/**
 * CPC (cost per click), USD. LOWER is better. Meta is objective-segmented
 * because the cited source publishes traffic and leads campaigns separately
 * (they are different auctions — collapsing them would misgrade both).
 */
export const PAID_SOCIAL_CPC_BENCHMARK: BenchmarkSet = {
  id: 'paid-social-cpc',
  metricLabel: 'CPC',
  unit: 'currency',
  higherIsBetter: false,
  glossaryUrl: `${SITE}/resources/glossary/metrics/cost-per-click-cpc`,
  segments: [
    {
      id: 'meta-traffic',
      platform: 'meta',
      objective: 'Traffic',
      segment: 'Meta (Facebook) · traffic objective',
      low: 0.34,
      median: 0.70,
      high: 1.22,
      sources: ['WordStream/LocaliQ 2025 Facebook Ads Benchmarks (Apr 2024–Jun 2025 medians)'],
      note: 'Band spans the lowest ($0.34, Shopping/Collectibles & Gifts) to highest ($1.22, Finance & Insurance) industry medians; $0.70 is the cross-industry median.',
    },
    {
      id: 'meta-leads',
      platform: 'meta',
      objective: 'Leads',
      segment: 'Meta (Facebook) · leads objective',
      low: 0.74,
      median: 1.92,
      high: 9.78,
      sources: ['WordStream/LocaliQ 2025 Facebook Ads Benchmarks (Apr 2024–Jun 2025 medians)'],
      note: 'Band spans the lowest ($0.74, Restaurants & Food) to highest ($9.78, Dentists & Dental Services) industry medians — high-LTV service verticals stretch the top far above the $1.92 cross-industry median.',
    },
    {
      id: 'tiktok',
      platform: 'tiktok',
      segment: 'TikTok · In-Feed',
      low: 0.74,
      median: 1.02,
      high: 1.92,
      sources: ['Digital Applied 2026 TikTok Ads Benchmarks (in-feed industry span)'],
      note: 'Band spans the lowest ($0.74, Beauty & Personal Care) to highest ($1.92, Legal Services) industry figures. Lebesgue’s broader 2026 typical range is $0.20–$2.00 depending on bidding and audience.',
    },
    {
      id: 'linkedin',
      platform: 'linkedin',
      segment: 'LinkedIn · Sponsored Content',
      low: 2.50,
      median: 3.94,
      high: 8.04,
      sources: ['Closely 2026 LinkedIn Ad Benchmarks (aggregated 2025 reports)'],
      note: 'Median CPC $3.94; band spans the cheapest cited industry (Finance & Insurance, from $2.50) to the most expensive (SaaS/Software, up to $8.04). US-only campaigns typically price above the global figures.',
    },
    // YouTube CPC deliberately omitted — see the module docblock (the only
    // current published figure is a full observed span, not a typical band).
  ],
}

/**
 * CTR (click-through rate), %. HIGHER is better. Meta is objective-segmented
 * (traffic vs leads) matching the cited report. Percent anchors keep the
 * source's sub-1% precision exactly (LinkedIn 0.42 / 0.52 / 0.80).
 */
export const PAID_SOCIAL_CTR_BENCHMARK: BenchmarkSet = {
  id: 'paid-social-ctr',
  metricLabel: 'CTR',
  unit: 'percent',
  higherIsBetter: true,
  glossaryUrl: `${SITE}/resources/glossary/metrics/click-through-rate-ctr`,
  segments: [
    {
      id: 'meta-traffic',
      platform: 'meta',
      objective: 'Traffic',
      segment: 'Meta (Facebook) · traffic objective',
      low: 0.83,
      median: 1.71,
      high: 4.13,
      sources: ['WordStream/LocaliQ 2025 Facebook Ads Benchmarks (Apr 2024–Jun 2025 medians)'],
      note: 'Band spans the lowest (0.83%, Physicians & Surgeons) to highest (4.13%, Shopping/Collectibles & Gifts) industry medians; 1.71% is the cross-industry median, up from 1.57% the prior year.',
    },
    {
      id: 'meta-leads',
      platform: 'meta',
      objective: 'Leads',
      segment: 'Meta (Facebook) · leads objective',
      low: 1.05,
      median: 2.59,
      high: 3.92,
      sources: ['WordStream/LocaliQ 2025 Facebook Ads Benchmarks (Apr 2024–Jun 2025 medians)'],
      note: 'Band spans the lowest (1.05%, Dentists & Dental Services) to highest (3.92%, Arts & Entertainment) industry medians; 2.59% is the cross-industry median.',
    },
    {
      id: 'tiktok',
      platform: 'tiktok',
      segment: 'TikTok · In-Feed',
      low: 0.5,
      median: 1.0,
      high: 1.5,
      sources: [
        'Lebesgue 2026 TikTok Ads Benchmarks (typical range)',
        'Digital Applied 2026 TikTok Ads Benchmarks (cross-industry average)',
      ],
      note: 'Lebesgue’s conversion-optimized e-commerce dataset averages 0.61% while Triple Whale’s 2025 cross-brand dataset averages 1.77% — objective and vertical mix move TikTok CTR more than most platforms, so read the verdict directionally.',
    },
    {
      id: 'linkedin',
      platform: 'linkedin',
      segment: 'LinkedIn · Sponsored Content',
      low: 0.42,
      median: 0.52,
      high: 0.80,
      sources: ['Closely 2026 LinkedIn Ad Benchmarks (aggregated 2025 reports)'],
      note: 'Median 0.52%; band spans the lowest cited industry (Education, 0.42%) to the highest (Retail, 0.80%). Single-image Sponsored Content typically lands at 0.50%–0.60%.',
    },
    {
      id: 'youtube',
      platform: 'youtube',
      segment: 'YouTube · skippable in-stream',
      low: 0.5,
      median: 0.65,
      high: 2.0,
      sources: ['Store Growers 2026 YouTube Ads Benchmarks (updated Feb 2026)'],
      note: 'Overall average 0.65% with skippable in-stream typically 0.5%–2.0% — YouTube CTR behaves like display, not search; view rate is the primary engagement metric for in-stream.',
    },
  ],
}

/** Metric id → dataset, in display order. */
export const PAID_SOCIAL_METRICS: Record<PaidSocialMetricId, BenchmarkSet> = {
  cpm: PAID_SOCIAL_CPM_BENCHMARK,
  cpc: PAID_SOCIAL_CPC_BENCHMARK,
  ctr: PAID_SOCIAL_CTR_BENCHMARK,
}

/**
 * Ordered platform ids that have at least one cited band for `metricId` —
 * DERIVED from the dataset, never hardcoded, so the platform selector can
 * only ever offer platforms the selected metric actually covers (coverage is
 * deliberately ragged: e.g. YouTube has no CPC band).
 */
export function paidSocialPlatformsFor(metricId: PaidSocialMetricId): PaidSocialPlatformId[] {
  const present = new Set(PAID_SOCIAL_METRICS[metricId].segments.map((s) => s.platform))
  return PAID_SOCIAL_PLATFORM_IDS.filter((id) => present.has(id))
}

/**
 * Ordered objective labels cited for (metric, platform), or `[]` when the
 * source publishes one blended band (no objective selector should render).
 */
export function paidSocialObjectivesFor(
  metricId: PaidSocialMetricId,
  platformId: PaidSocialPlatformId,
): string[] {
  const objectives: string[] = []
  for (const s of PAID_SOCIAL_METRICS[metricId].segments) {
    if (s.platform === platformId && s.objective && !objectives.includes(s.objective)) {
      objectives.push(s.objective)
    }
  }
  return objectives
}

/**
 * Formats a cited anchor at the SOURCE'S precision for cited-table surfaces
 * (SSR tables, md twin parity): currency keeps conventional cents for
 * fractional values ("$5.40") and whole dollars stay whole ("$31"); percent
 * truncates (never rounds) at two decimals and drops trailing zeros
 * ("0.42%", "0.8%") — so a sub-1% CTR can never display coarser than cited
 * (the #327 precision loss, `r3413662650`).
 */
export function formatPaidSocialAnchor(value: number, unit: BenchmarkSet['unit']): string {
  if (unit === 'currency') {
    return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`
  }
  const normalized = Math.round(value * 1e6) / 1e6
  const truncated = Math.floor(normalized * 100) / 100
  return `${truncated}%`
}

/** One fully-resolved, mutually-consistent selection. */
export interface PaidSocialSelection {
  metricId: PaidSocialMetricId
  set: BenchmarkSet
  /** The platform actually in effect — always one `metricId` has a band for. */
  platformId: PaidSocialPlatformId
  /** The objective actually in effect (undefined when the band is blended). */
  objective: string | undefined
  /** The segment for (platformId, objective) — never a silent fallback to a
   *  different platform than `platformId`. */
  segment: BenchmarkSegment
}

/**
 * Resolves a (metric, requested platform, requested objective) trio into one
 * coherent selection. TOTAL and PURE — this is the anti-desync contract the
 * original PR #327 tool lacked (its `platformId` state could point at a
 * platform the active metric had no data for, silently comparing against
 * Meta while the UI implied YouTube — `r3413612089`):
 *
 *   1. If `metricId` has no band for `requestedPlatform`, the platform is
 *      COERCED to the metric's first covered platform — and the coerced id is
 *      RETURNED, so the caller renders selector, verdict, and labels from the
 *      same resolved value. UI and math cannot disagree.
 *   2. Same rule for the objective axis (a platform whose bands are blended
 *      resolves to `objective: undefined`; a requested objective the source
 *      doesn't split by coerces to the first cited objective).
 *   3. The returned `segment` is guaranteed to belong to the returned
 *      `platformId`/`objective` (unit-tested invariant).
 *
 * Callers keep the user's RAW platform/objective preferences in state and
 * resolve on every render — so switching metric A → B → A restores the
 * platform chosen on A instead of destroying it.
 */
export function resolvePaidSocialSelection(
  metricId: PaidSocialMetricId,
  requestedPlatform: PaidSocialPlatformId,
  requestedObjective?: string,
): PaidSocialSelection {
  const set = PAID_SOCIAL_METRICS[metricId]
  const platforms = paidSocialPlatformsFor(metricId)
  const fallbackPlatform = platforms[0]
  if (!fallbackPlatform) {
    // Every dataset above declares ≥1 segment; this guards future edits.
    throw new Error(`paid-social benchmark set "${metricId}" has no segments`)
  }
  const platformId = platforms.includes(requestedPlatform) ? requestedPlatform : fallbackPlatform

  const objectives = paidSocialObjectivesFor(metricId, platformId)
  const objective =
    objectives.length === 0
      ? undefined
      : requestedObjective !== undefined && objectives.includes(requestedObjective)
        ? requestedObjective
        : objectives[0]

  const segment = set.segments.find(
    (s) => s.platform === platformId && (objective === undefined || s.objective === objective),
  )
  if (!segment) {
    // Unreachable by construction (platformId/objective are derived from the
    // segments themselves) — kept as a loud guard against future data edits.
    throw new Error(
      `paid-social benchmark set "${metricId}" has no segment for platform "${platformId}"${
        objective ? ` × objective "${objective}"` : ''
      }`,
    )
  }
  return { metricId, set, platformId, objective, segment }
}
